import React, { useState, useEffect } from 'react'
import { PublicClientApplication } from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom'
import { useDispatch } from "react-redux";
import successToast from '../toasts/successToast';
import errorToast from '../toasts/errorToast';
import { microsoftClientId, microsoftTenantId } from '../../redux/config';
import { Spinner } from 'react-bootstrap';
import { microsoftLoginAction } from '../../redux/actions/authAction';
import { useAuth } from '../../context/AuthContext';

const msalInstance = new PublicClientApplication({
    auth: {
        clientId: microsoftClientId,
        authority: `https://login.microsoftonline.com/${microsoftTenantId}`,
        redirectUri: window.location.origin,
    },
});
let msalInitPromise = null;

const MicrosoftLogo = ({ size = 21 }) => (
    <svg width={size} height={size} viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect x="1" y="1" width="9" height="9" fill="#F25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
        <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
);

const MicrosoftLoginButton = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { fetchMe } = useAuth();
    const [loading, setLoading] = useState(false)
    const [account, setAccount] = useState(null)
    const [checkingAccount, setCheckingAccount] = useState(true)

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (!msalInitPromise) msalInitPromise = msalInstance.initialize();
                await msalInitPromise;
                const accounts = msalInstance.getAllAccounts();
                if (!cancelled && accounts.length > 0) {
                    setAccount(accounts[0]);
                }
            } catch (err) {
                // ignore - falls back to the generic button
            } finally {
                if (!cancelled) setCheckingAccount(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const onSuccess = async (data) => {
        if (data.data.data.requires_2fa) {
            sessionStorage.setItem('2fa_session_token', data.data.data.session_token);
            setLoading(false);
            navigate('/verify-2fa');
            return;
        }
        await fetchMe();
        setLoading(false);
        localStorage.setItem("active_tab", 'Overview');
        successToast(data.data.message);
        navigate('/over-view');
    }

    const onError = (data) => {
        setLoading(false)
        errorToast(data?.data?.message || 'Login Failed');
    }

    const completeLogin = (idToken) => {
        dispatch(microsoftLoginAction({ access_token: idToken }, onSuccess, onError));
    }

    const handleMicrosoftLogin = async () => {
        try {
            setLoading(true);
            if (!msalInitPromise) msalInitPromise = msalInstance.initialize();
            await msalInitPromise;
            const loginResponse = await msalInstance.loginPopup({
                scopes: ['openid', 'profile', 'email'],
            });
            completeLogin(loginResponse.idToken);
        } catch (err) {
            setLoading(false);
            errorToast('Microsoft login failed');
        }
    }

    const handleContinueAsAccount = async (acc) => {
        try {
            setLoading(true);
            const result = await msalInstance.ssoSilent({
                scopes: ['openid', 'profile', 'email'],
                account: acc,
            });
            completeLogin(result.idToken);
        } catch (err) {
            // Cached session expired/invalid - fall back to an interactive popup for this same account
            try {
                const result = await msalInstance.loginPopup({
                    scopes: ['openid', 'profile', 'email'],
                    loginHint: acc.username,
                });
                completeLogin(result.idToken);
            } catch (popupErr) {
                setLoading(false);
                errorToast('Microsoft login failed');
            }
        }
    }

    if (checkingAccount) return null;

    return (
        <div className='d-flex flex-column justify-content-center align-items-center'>
            {loading && <div className='d-flex justify-content-center w-100 mb-2'> <Spinner size='sm' className='text-primary' /> <span className='fs_14 fw_400 ms-2'>Logging in...</span></div>}

            {account ? (
                <>
                    <button
                        type='button'
                        onClick={() => handleContinueAsAccount(account)}
                        disabled={loading}
                        onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            width: '100%',
                            maxWidth: 300,
                            padding: '4px 10px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #dadce0',
                            borderRadius: 4,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1,
                            transition: 'background-color 0.15s ease',
                            fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Arial, sans-serif",
                        }}
                    >
                        <div style={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            backgroundColor: '#0078D4',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 14,
                            fontWeight: 600,
                            flexShrink: 0,
                        }}>
                            {(account.name || account.username || '?').charAt(0).toUpperCase()}
                        </div>
                        <div style={{ textAlign: 'left', flex: 1, overflow: 'hidden' }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: '#1f1f1f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                Continue as {account.name || account.username}
                            </div>
                            <div style={{ fontSize: 11, color: '#5e5e5e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {account.username}
                            </div>
                        </div>
                        <MicrosoftLogo size={20} />
                    </button>
                    <button
                        type='button'
                        onClick={() => setAccount(null)}
                        disabled={loading}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#5e5e5e',
                            fontSize: 12,
                            marginTop: 4,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                        }}
                    >
                        Use another account
                    </button>
                </>
            ) : (
                <button
                    type='button'
                    onClick={handleMicrosoftLogin}
                    disabled={loading}
                    onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#f3f2f1'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 12,
                        width: '100%',
                        maxWidth: 300,
                        height: 41,
                        padding: '0 12px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #8c8c8c',
                        borderRadius: 4,
                        color: '#5e5e5e',
                        fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Arial, sans-serif",
                        fontSize: 15,
                        fontWeight: 600,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.7 : 1,
                        transition: 'background-color 0.15s ease',
                    }}
                >
                    <MicrosoftLogo />
                    <span>Sign in with Microsoft</span>
                </button>
            )}
        </div>
    )
}

export default MicrosoftLoginButton
